/*
The page-leave transport (XOL-596, plan R2.18 B): a content save (or, for a pending
decision field, an approval command) sent with
`fetch(..., {keepalive: true})`, so it can outlive the page that is being closed
or reloaded (`beforeunload` / `pagehide` cannot await an axios request).

This is a NON-AXIOS `/api/*` call, so nothing is inherited: the headers axios would
add are set here, explicitly (CLAUDE.md "Non-axios /api/* calls"):

  Content-Type   application/json
  X-API-Key      src/lib/env.js API_KEY (the XOLAS_API_KEY build arg)
  Authorization  Bearer <the access token>, as the axios request interceptor sends
  X-Request-Id   one per request (generateRequestId, so the API log correlates it)

`credentials: 'include'` lets the xolas_auth cookie flow, as `withCredentials`
does for axios. There is no refresh at close: an expired access token answers 401
and that save is lost (stated limit). A body over the keepalive budget is never
started (lib/requestRowSync.startKeepaliveSaves). Dev is cross-origin
(SPA :8080 to API :8000), so the PUT needs a CORS preflight; browser support for a
preflighted keepalive request has varied and is UNVERIFIED here. Production is
same-origin and has no preflight.

The answer is turned into the shape the save handlers already read: resolve
`{status, data}` on 2xx; reject `{response: {status, data}, config: {headers}}` on
any other status, and a bare Error (no `response`) when the request itself failed.
ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import { API_KEY } from '@/lib/env'
import { getAccessToken } from '@/lib/auth'
import { generateRequestId } from '@/lib/axiosClient'

export function keepaliveHeaders (requestId) {
  var headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': API_KEY,
    'X-Request-Id': requestId
  }
  var token = getAccessToken()
  if (token) {
    headers['Authorization'] = 'Bearer ' + token
  }
  return headers
}

function keepaliveRequest (method, url, body) {
  var requestId = generateRequestId()
  var headers = keepaliveHeaders(requestId)

  return window.fetch(url, {
    method: method,
    keepalive: true,
    credentials: 'include',
    headers: headers,
    body: JSON.stringify(body)
  }).then(function (response) {
    return response.text().then(function (text) {
      var data = {}
      try {
        data = text ? JSON.parse(text) : {}
      } catch (e) {
        data = {}
      }
      if (response.status >= 200 && response.status < 300) {
        return { status: response.status, data: data, requestId: requestId }
      }
      return Promise.reject({
        message: 'Request failed with status code ' + response.status,
        config: { headers: headers },
        response: { status: response.status, data: data }
      })
    })
  })
}

/**
 * PUT `body` (a v3 content body) to the full `url` with keepalive.
 *
 * @return {Promise<{status, data, requestId}>}
 */
export function keepalivePut (url, body) {
  return keepaliveRequest('PUT', url, body)
}

/**
 * POST `body` (an approval command body: a pending decision field, XOL-596) to the
 * full `url` with keepalive. Same headers and answer shape as keepalivePut.
 */
export function keepalivePost (url, body) {
  return keepaliveRequest('POST', url, body)
}
